import { Injectable, inject } from '@angular/core';
import { CoreActions } from '@next/core-lib';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, finalize, map, mergeMap, startWith, switchMap } from 'rxjs/operators';

import * as MeasurementsActions from './measurements.actions';
import { CustomHttpErrorResponse, FormMode, SiteState } from '../../shared/models/models';
import { MeasurementWizardComponent } from '../components/measurement-wizard/measurement-wizard.component';

import { Measurement, RationType, Species, Type } from '../models/measurements.model';
import { RuminantsAnalysisService } from '../services/ruminants-analysis.service';

@Injectable()
export class MeasurementsEffects {
  private readonly actions$ = inject(Actions);
  private readonly ruminantsAnalysisApi = inject(RuminantsAnalysisService);
  private readonly dialogService = inject(DialogService);
  private readonly localeService = inject(LocaleService);
  private readonly store = inject<Store<SiteState>>(Store);

  loadMeasurements$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasurementsActions.loadMeasurementOverviewRequest),
      mergeMap(() =>
        this.ruminantsAnalysisApi.getMeasurements().pipe(
          map((measurementOverview) =>
            MeasurementsActions.loadMeasurementOverviewSuccess({
              measurementOverview,
            }),
          ),
          catchError((errorResponse: CustomHttpErrorResponse) => {
            const message = errorResponse.error?.message
              ? this.localeService.translate(errorResponse.error.message)
              : errorResponse.message;
            return of(NotificationActions.errorSnackbar({ message }));
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadMeasurement$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasurementsActions.loadMeasurementRequest),
      map((action) => action.measurementId),
      mergeMap((measurementId: number) =>
        this.ruminantsAnalysisApi.getMeasurement(measurementId).pipe(
          map((measurement) => {
            this.dialogService
              .form(MeasurementWizardComponent, {
                mode: FormMode.EDIT,
                setPendingChanges: false,
              })
              .subscribe(({ status, result }) => {
                if (status === DIALOG_RETURN_TYPES.confirm) {
                  this.store.dispatch(
                    MeasurementsActions.updateMeasurementRequest({
                      measurement: result.measurement,
                    }),
                  );
                }
                this.store.dispatch(MeasurementsActions.clearMeasurement());
              });

            return MeasurementsActions.loadMeasurementSuccess({
              measurement,
            });
          }),
          catchError((errorResponse: CustomHttpErrorResponse) => {
            const message = errorResponse.error?.message
              ? this.localeService.translate(errorResponse.error.message)
              : errorResponse.message;
            return of(NotificationActions.errorSnackbar({ message }));
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  calculateMeasurement$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasurementsActions.calculateMeasurementRequest),
      map((action) => action.measurement),
      switchMap((measurement: Measurement) =>
        this.ruminantsAnalysisApi.postCalculate(measurement).pipe(
          map((calculatedMeasurement) =>
            MeasurementsActions.calculateMeasurementSuccess({
              measurement: calculatedMeasurement,
            }),
          ),
          catchError((errorResponse: CustomHttpErrorResponse) => {
            const message = errorResponse.error?.message
              ? this.localeService.translate(errorResponse.error.message)
              : errorResponse.message;
            return of(NotificationActions.errorSnackbar({ message }));
          }),
        ),
      ),
    ),
  );

  initializeMeasurement$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasurementsActions.initializeMeasurementRequest),
      map(() => {
        this.dialogService
          .form(MeasurementWizardComponent, {
            mode: FormMode.CREATE,
            setPendingChanges: false,
          })
          .subscribe(({ status, result }) => {
            if (status === DIALOG_RETURN_TYPES.confirm) {
              this.store.dispatch(
                MeasurementsActions.addMeasurementRequest({ measurement: result.measurement }),
              );
            }
            this.store.dispatch(MeasurementsActions.clearMeasurement());
          });

        return MeasurementsActions.initializeMeasurementSuccess({
          measurement: {
            type: Type.Ration,
            rationType: RationType.Pmr,
            species: Species.MilkCow,
          },
        });
      }),
    ),
  );

  copyMeasurement$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasurementsActions.copyMeasurementRequest),
      map((action) => action.existingMeasurementSummaryId),
      mergeMap((existingMeasurementSummaryId: number) =>
        this.ruminantsAnalysisApi.getMeasurement(existingMeasurementSummaryId).pipe(
          map((existingMeasurement) => {
            this.dialogService
              .form(MeasurementWizardComponent, {
                mode: FormMode.CREATE,
                setPendingChanges: false,
              })
              .subscribe(({ status, result }) => {
                if (status === DIALOG_RETURN_TYPES.confirm) {
                  this.store.dispatch(
                    MeasurementsActions.addMeasurementRequest({ measurement: result.measurement }),
                  );
                }
                this.store.dispatch(MeasurementsActions.clearMeasurement());
              });

            return MeasurementsActions.copyMeasurementSuccess({
              measurement: {
                name:
                  existingMeasurement.name +
                  ' ' +
                  this.localeService.translate('measurements.overview.restfeed-suffix'),
                type: Type.Restfeed,
                species: existingMeasurement.species,
                relatedMeasurementId: existingMeasurement.id,
                input: {
                  dmPercentage: existingMeasurement.input.dmPercentage,
                  ndf: existingMeasurement.input.ndf,
                },
              },
            });
          }),
          catchError((errorResponse: CustomHttpErrorResponse) => {
            const message = errorResponse.error?.message
              ? this.localeService.translate(errorResponse.error.message)
              : errorResponse.message;
            return of(NotificationActions.errorSnackbar({ message }));
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  addMeasurement$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasurementsActions.addMeasurementRequest),
      map((action) => action.measurement),
      mergeMap((measurement: Measurement) =>
        this.ruminantsAnalysisApi.postMeasurement(measurement).pipe(
          mergeMap((updatedMeasurement) =>
            of(
              MeasurementsActions.addMeasurementSuccess({
                measurement: updatedMeasurement,
              }),
              MeasurementsActions.loadMeasurementOverviewRequest(),
            ),
          ),
          catchError((errorResponse: CustomHttpErrorResponse) => {
            const message = errorResponse.error?.message
              ? this.localeService.translate(errorResponse.error.message)
              : errorResponse.message;
            return of(NotificationActions.errorSnackbar({ message }));
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  updateMeasurement$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasurementsActions.updateMeasurementRequest),
      map((action) => action.measurement),
      mergeMap((measurement: Measurement) =>
        this.ruminantsAnalysisApi.updateMeasurement(measurement).pipe(
          mergeMap((updatedMeasurement) =>
            of(
              MeasurementsActions.updateMeasurementSuccess({
                measurement: updatedMeasurement,
              }),
              MeasurementsActions.loadMeasurementOverviewRequest(),
            ),
          ),
          catchError((errorResponse: CustomHttpErrorResponse) => {
            const message = errorResponse.error?.message
              ? this.localeService.translate(errorResponse.error.message)
              : errorResponse.message;
            return of(NotificationActions.errorSnackbar({ message }));
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deleteMeasurement$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasurementsActions.deleteMeasurementRequest),
      map((action) => action.measurementId),
      mergeMap((measurementId: number) =>
        this.ruminantsAnalysisApi.deleteMeasurement(measurementId).pipe(
          mergeMap(() =>
            of(
              MeasurementsActions.deleteMeasurementSuccess({ measurementId }),
              MeasurementsActions.loadMeasurementOverviewRequest(),
            ),
          ),
          catchError((errorResponse: CustomHttpErrorResponse) => {
            const message = errorResponse.error?.message
              ? this.localeService.translate(errorResponse.error.message)
              : errorResponse.message;
            return of(NotificationActions.errorSnackbar({ message }));
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );
}
